import React from "react";

import SideBySide from "./newsletter.template.sideBySide";
import Stack from "./newsletter.template.stack";
import { subscribeToConnect } from "./newsletter.service";

import { ModuleWrapperV2 } from "src/components/moduleWrapper/moduleWrapper.v2";
import SiteContainer from "src/components/siteContainer/siteContainer";
import BackgroundImageWrapper from "src/components/backgroundImageWrapper/backgroundImageWrapper";

import { getLanguageField } from "src/utils/language";

export default class Newsletter extends React.Component<NewsletterProps, NewsletterState>{
  state: NewsletterState = {
    subscribingToMail: false,
    email: "",
    feedback: "none",
  }

  get isFullWidth(){
    return this.props.data.item.width === "full_screen";
  }

  get padding() {
    const { padding_bottom, padding_top} = this.props.data.item;
    return { top: padding_top, bottom: padding_bottom };
  }

  get rounded(){
    const { rounded_corners, width } = this.props.data.item;
    return width === "boxed" && rounded_corners;
  }

  get innerPadding() {
    if(this.isFullWidth) return "px-6 xl:px-0"
    return "px-4 sm:px-6 lg:px-8"
  }

  get overlayColor(){
    const { overlay, background_overlay_opacity, background_color_overlay } = this.props.data.item
    if( overlay && background_color_overlay?.value && background_overlay_opacity) return background_color_overlay.value +  Math.floor( background_overlay_opacity / 100 * 255 || 0 * 255).toString(16);
    return "";
  }

  get outterMargin() {
    if (this.isFullWidth) return 'mx-auto my-auto';
    return 'mx-4 sm:mx-6 xl:mx-0 my-8 lg:my-12';
  }

  get renderTemplate(){
    const { selectedLanguage } = this.props.data;
    const { email } = this.state;
    const { layout, translations, alignment, input_position, body_color, title_color, disclaimer_text_color,
      cta_button_background_color, cta_button_hover_background_color, cta_button_label_color, cta_button_style,
      cta_button_hover_label_color,  cta_button_hover_outline_color, cta_button_outline_color, success_feedback_color,
      error_feedback_color
    } = this.props.data.item;
    const title = getLanguageField(translations, selectedLanguage, "title") as string | undefined;
    const body = getLanguageField(translations, selectedLanguage, "body", true, true) as string | undefined;
    const disclaimer = getLanguageField(translations, selectedLanguage, "disclaimer", true, true) as string | undefined;
    const inputPlaceholder = getLanguageField(translations, selectedLanguage, "input_placeholder_text") as string | undefined;
    const buttonText = getLanguageField(translations, selectedLanguage, "cta_label") as string | undefined;
    const ctaButtonProps = {
      bgcolor: cta_button_background_color?.value,
      hoverbgcolor: cta_button_hover_background_color?.value,
      color: cta_button_label_color?.value,
      border: cta_button_outline_color?.value,
      hovercolor: cta_button_hover_label_color?.value,
      hoverborder: cta_button_hover_outline_color?.value,
      text: buttonText,
      loading: this.state.subscribingToMail,
      type: cta_button_style,
      useButton: true,
    } as any;

    const newsletterComponentProps: NewsletterComponentCommonProps = {
      title: title,
      description: body,
      disclaimer: disclaimer,
      language: selectedLanguage,
      defaultLanguage: this.props.defaultLanguage,
      alignment: alignment,
      body_color: body_color?.value,
      disclaimer_text_color: disclaimer_text_color?.value,
      ctaButton: ctaButtonProps,
      email: email,
      setEmailValue: this.setEmailValue,
      onSubmit: () => this.subscribeToNewsletter(email),
      feedback: this.state.feedback,
      inputPlaceholder: inputPlaceholder,
      title_color: title_color?.value,
      textPosition: input_position,
      error_feedback_color: error_feedback_color?.value,
      success_feedback_color: success_feedback_color?.value,
      email_focus_color: cta_button_background_color?.value
    }

    if(layout === "side-by-side") return <SideBySide {...newsletterComponentProps} />;
    if(layout === "stack") return <Stack {...newsletterComponentProps} />;
    return null;
  }

  getBackgroundImageProps(wrapper:"background-image-wrapper" | "module-wrapper" = "background-image-wrapper"){
    const { background_image, background_image_placement } = this.props.data.item;

    const props = {
      backgroundImage: background_image?.imageFile,
      overlayColor: this.overlayColor
    }
    if(wrapper === "background-image-wrapper" && background_image_placement === "full_screen" || this.isFullWidth) return props;
    if(wrapper === "module-wrapper" && background_image_placement === "boxed") return props;
    return {};
  }

  setEmailValue = (email: string) => {
    this.setState({ email });
  }

  subscribeToNewsletter = (email: string) => {
    this.setState({ subscribingToMail: true, feedback: "none" }, async () => {
      const response = await subscribeToConnect(email, this.props.data.selectedLanguage, this.props.data.item.external_integration?.id);
      let feedback: NewsletterFeedback = "error";
      if (response.status === "ok") { feedback = "subscribed"; }
      if (response.status !== "ok" && response.message === "BAD_EMAIL") { feedback = "bad.email.format"; }
      if (response.status !== "ok" && response.message === "EXISTING_EMAIL") { feedback = "already.subscribed"; }
      this.setState({
        subscribingToMail: false,
        email: feedback === "subscribed" ? "" : this.state.email,
        feedback: feedback,
      })
    })
  }

  get style(): React.CSSProperties & TCSSVars {
    return {
      '--hyperlink-text-color': this.props.data.item.hyperlink_text_color?.value,
      '--hyperlink-hover-color': this.props.data.item.hyperlink_hover_color?.value,
    };
  }

  render(): React.ReactNode {
    const { translations, background_color, name, boxed_background_color } = this.props.data.item;
    const { selectedLanguage } = this.props.data;

    return (
      <BackgroundImageWrapper {...this.getBackgroundImageProps("background-image-wrapper")} backgroundColor={background_color?.value}>
        <SiteContainer fullwidth={this.isFullWidth}>
          <ModuleWrapperV2
            anchor={getLanguageField(translations, selectedLanguage, "anchor") as string}
            name={name}
            backgroundColor={boxed_background_color?.value}
            paddings={this.padding}
            backgroundImage={undefined}
            roundedCorners={this.rounded}
            className={`${this.outterMargin} ${this.innerPadding}`}
            style={this.style}
            {...this.getBackgroundImageProps("module-wrapper")}
          >
            {this.renderTemplate}
          </ModuleWrapperV2>
        </SiteContainer>
      </BackgroundImageWrapper>
    )
  }
}